import React from "react";
import uiConfig from "../config/uiConfig";

import {
  Avatar,
  Box,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";

import DashboardIcon from "@mui/icons-material/Dashboard";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import AccountTreeIcon from "@mui/icons-material/AccountTree";

import { useMsal } from "@azure/msal-react";
import { NavLink } from "react-router-dom";
import { canAccessPage } from "../auth/roles";
import { useUserRole } from "../context/UserRoleContext";
import { useColorMode } from "../context/ColorModeContext";
import faviconIcon from "../assets/img/favicon.png";
import aaysLogo from "../assets/img/Aays.png";

const menuItems = [
  {
    text: "Dashboard",
    icon: <DashboardIcon />,
    path: "/dashboard",
    pageKey: "dashboard",
  },
  {
    text: "Tickets",
    icon: <ConfirmationNumberIcon />,
    path: "/tickets",
    pageKey: "tickets",
  },
  {
    text: "Projects",
    icon: <AccountTreeIcon />,
    path: "/projects",
    pageKey: "projects",
  },
];

function Sidebar({
  drawerWidth,
  collapsedDrawerWidth,
  isSidebarCollapsed,
  onResizeStart,
  mobileOpen,
  handleDrawerToggle,
}) {
  const { accounts } = useMsal();
  const { role } = useUserRole();
  const { mode } = useColorMode();

  const sidebarBackground = mode === "dark"
    ? "linear-gradient(180deg, #1a1025 0%, #0d0812 100%)"
    : uiConfig.dialogHeaderGradient;

  const sidebarBackgroundSolid = mode === "dark" ? "#150f1e" : uiConfig.sidebarBackgroundColor;

  const currentAccount = accounts?.[0];
  const userName = currentAccount?.name || "Guest User";
  const userEmail = currentAccount?.username || "Not signed in";

  const initials = userName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "GU";

  const visibleMenuItems = menuItems.filter((item) => canAccessPage(role, item.pageKey));

  const renderDrawer = (collapsed = false) => (
    <>
      <Toolbar
        sx={{
          minHeight: "70px",
          px: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "flex-start",
        }}
      >
        {collapsed ? (
          <Box
            component="img"
            src={faviconIcon}
            alt="Aays"
            sx={{ width: 32, height: 32 }}
          />
        ) : (
          <Box
            component="img"
            src={aaysLogo}
            alt="Aays"
            sx={{ height: 32, width: "auto" }}
          />
        )}
      </Toolbar>

      <List sx={{ mt: 1, flexGrow: 1, overflowY: "auto" }}>
        {visibleMenuItems.map((item) => (
          <NavLink
            key={item.text}
            to={item.path}
            style={{
              textDecoration: "none",
            }}
          >
            {({ isActive }) => (
              <ListItemButton
                sx={{
                  mx: 1,
                  mb: 0.5,
                  minHeight: 48,
                  px: 2,
                  justifyContent: collapsed ? "center" : "flex-start",
                  borderRadius: "10px",
                  backgroundColor: isActive ? uiConfig.sidebarHighlightColor : "transparent",
                  "&:hover": {
                    backgroundColor: isActive ? uiConfig.sidebarHighlightColor : "rgba(255,255,255,0.08)",
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: "white",
                    minWidth: collapsed ? "auto" : 40,
                  }}
                >
                  {item.icon}
                </ListItemIcon>

                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontSize: 16,
                    fontWeight: isActive ? 600 : 400,
                    color: "white",
                  }}
                  sx={{
                    display: collapsed ? "none" : "block",
                  }}
                />
              </ListItemButton>
            )}
          </NavLink>
        ))}
      </List>

      <Box sx={{ mt: "auto", px: 1.5, pb: 1.5 }}>
        <Divider sx={{ borderColor: "rgba(255,255,255,0.14)", mb: 1.2 }} />

        <Tooltip
          title={collapsed ? `${userName} (${userEmail})` : ""}
          placement="right"
          arrow
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: collapsed ? "center" : "flex-start",
              gap: 1.2,
              borderRadius: 2,
              px: collapsed ? 0.8 : 1,
              py: 1,
              background: "rgba(255,255,255,0.06)",
            }}
          >
            <Avatar
              sx={{
                width: 34,
                height: 34,
                fontSize: 13,
                bgcolor: uiConfig.sidebarHighlightColor,
                fontWeight: 700,
              }}
            >
              {initials}
            </Avatar>

            {!collapsed && (
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    color: "#ffffff",
                    fontSize: 13,
                    fontWeight: 600,
                    lineHeight: 1.2,
                  }}
                  noWrap
                >
                  {userName}
                </Typography>

                <Typography
                  sx={{
                    color: "#cbd5e1",
                    fontSize: 11,
                    lineHeight: 1.2,
                  }}
                  noWrap
                >
                  {userEmail}
                </Typography>
              </Box>
            )}
          </Box>
        </Tooltip>
      </Box>
    </>
  );

  return (
    <Box
      component="nav"
      sx={{
        height: "100vh",
        width: {
          sm: isSidebarCollapsed ? collapsedDrawerWidth : drawerWidth,
        },
        flexShrink: {
          sm: 0,
        },
      }}
    >
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: {
            xs: "block",
            sm: "none",
          },
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            background: sidebarBackgroundSolid,
          },
        }}
      >
        {renderDrawer(false)}
      </Drawer>

      <Drawer
        variant="permanent"
        open
        sx={{
          display: {
            xs: "none",
            sm: "block",
          },
          "& .MuiDrawer-paper": {
            width: isSidebarCollapsed ? collapsedDrawerWidth : drawerWidth,
            height: "100vh",
            boxSizing: "border-box",
            position: "fixed",
            top: 0,
            left: 0,
            display: "flex",
            flexDirection: "column",
            background: sidebarBackground,
            color: "white",
            overflowX: "hidden",
            transition: "width 0.2s ease",
          },
        }}
      >
        {renderDrawer(isSidebarCollapsed)}

        {!isSidebarCollapsed && (
          <Box
            onMouseDown={onResizeStart}
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              width: 8,
              height: "100%",
              cursor: "col-resize",
              zIndex: 2,
              "&:hover": {
                background: "rgba(255,255,255,0.12)",
              },
            }}
          />
        )}
      </Drawer>
    </Box>
  );
}

export default Sidebar;
